import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, Car, Wrench, Plus, Trash2, Zap, PawPrint, Camera, Upload, Eye, Heart, Contact, Building2 } from 'lucide-react';
import { User } from 'lucide-react';
import { toast } from 'sonner';

function PetPhotoAvatar({ petId, hasPetPhoto, getPetPhotoUrl }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!hasPetPhoto) return;
    const { url, token } = getPetPhotoUrl(petId);
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => blob && setSrc(URL.createObjectURL(blob)))
      .catch((err) => { console.error('Failed to load pet photo:', err); });
  }, [petId, hasPetPhoto, getPetPhotoUrl]);
  if (src) return <img src={src} alt="Pet" className="w-10 h-10 rounded-full object-cover border-2 border-orange-200" />;
  return <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center"><PawPrint className="w-4 h-4 text-orange-700" /></div>;
}

export function FamilyCard({ members, onAdd, onRemove }) {
  return (
    <Card className="shadow-lg border-0" data-testid="family-members-card">
      <CardHeader className="bg-gk-navy-light text-white rounded-t-xl flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Users className="w-5 h-5" />
          Family Members
          <Badge variant="outline" className="ml-1 text-white border-white/40">{members.length}/5</Badge>
        </CardTitle>
        {members.length < 5 && (
          <Button data-testid="add-family-button" size="sm" onClick={onAdd} className="bg-gk-teal hover:bg-gk-teal-hover">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        {members.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No family members added yet</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.id} data-testid={`family-member-${m.id}`} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center"><User className="w-4 h-4 text-blue-700" /></div>
                  <div>
                    <p className="font-semibold text-sm">{m.name}</p>
                    <p className="text-xs text-slate-500">{m.phone}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => onRemove(m.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PetsCard({ pets, onAdd, onRemove, onUploadPhoto, onUploadVaccinationDoc, onViewVaccinationDoc, getPetPhotoUrl }) {
  return (
    <Card className="shadow-lg border-0" data-testid="pets-card">
      <CardHeader className="bg-gk-navy-light text-white rounded-t-xl flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <PawPrint className="w-5 h-5" />
          My Pets
        </CardTitle>
        <Button data-testid="add-pet-button" size="sm" onClick={onAdd} className="bg-gk-teal hover:bg-gk-teal-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </CardHeader>
      <CardContent className="pt-4">
        {pets.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No pets added yet</p>
        ) : (
          <div className="space-y-2">
            {pets.map((p) => (
              <div key={p.id} data-testid={`pet-${p.id}`} className="p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PetPhotoAvatar petId={p.id} hasPetPhoto={!!p.pet_photo} getPetPhotoUrl={getPetPhotoUrl} />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm">{p.name}</p>
                        <Badge variant="outline" className="text-xs capitalize">{p.pet_type}</Badge>
                      </div>
                      <p className="text-xs text-slate-500">{p.breed || 'Breed not specified'}</p>
                      <Badge className={`text-[10px] mt-0.5 ${p.vaccination_status === 'verified' ? 'bg-emerald-600' : 'bg-amber-500'}`}>
                        {p.vaccination_status === 'verified' ? 'Vaccinated' : 'Vaccination Pending'}
                      </Badge>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => onRemove(p.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-orange-200 flex-wrap">
                  <Button data-testid={`upload-pet-photo-${p.id}`} size="sm" variant="outline" onClick={() => onUploadPhoto(p.id)} className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50">
                    <Camera className="w-3 h-3 mr-1" />
                    {p.pet_photo ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  {p.vaccination_document ? (
                    <>
                      <Button data-testid={`view-vacc-doc-${p.id}`} size="sm" variant="outline" onClick={() => onViewVaccinationDoc(p.id)} className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        <Eye className="w-3 h-3 mr-1" />
                        View Certificate
                      </Button>
                      <Button data-testid={`reupload-vacc-doc-${p.id}`} size="sm" variant="outline" onClick={() => onUploadVaccinationDoc(p.id)} className="text-xs h-7">
                        <Upload className="w-3 h-3 mr-1" />
                        Re-upload
                      </Button>
                    </>
                  ) : (
                    <Button data-testid={`upload-vacc-doc-${p.id}`} size="sm" variant="outline" onClick={() => onUploadVaccinationDoc(p.id)} className="text-xs h-7 border-orange-300 text-orange-700 hover:bg-orange-50">
                      <Upload className="w-3 h-3 mr-1" />
                      Upload Vaccination Certificate
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DailyHelpCard({ dailyHelp, onAdd, onRemove }) {
  return (
    <Card className="shadow-lg border-0" data-testid="daily-help-card">
      <CardHeader className="bg-gk-navy-light text-white rounded-t-xl flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Wrench className="w-5 h-5" />
          Daily Help
        </CardTitle>
        <Button data-testid="add-daily-help-button" size="sm" onClick={onAdd} className="bg-gk-teal hover:bg-gk-teal-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </CardHeader>
      <CardContent className="pt-4">
        {dailyHelp.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No daily help assigned</p>
        ) : (
          <div className="space-y-2">
            {dailyHelp.map((h) => (
              <div key={h.id} data-testid={`daily-help-${h.id}`} className="flex items-center justify-between p-3 bg-gk-bg rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center"><Wrench className="w-4 h-4 text-gk-navy" /></div>
                  <div>
                    <p className="font-semibold text-sm">{h.name}</p>
                    <p className="text-xs text-slate-500">{h.job_description} {h.phone && `| ${h.phone}`}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => onRemove(h.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export function VehiclesCard({ vehicles, onAdd, onRemove, config, onUploadPicture }) {
  const fourWheelers = vehicles.filter(v => v.vehicle_type === '4-wheeler');
  const twoWheelers = vehicles.filter(v => v.vehicle_type === '2-wheeler');
  const maxCars = config?.max_cars_per_villa ?? 3;
  const maxBikes = config?.max_bikes_per_villa ?? 1;

  const statusConfig = {
    approved: { label: 'Approved', className: 'bg-emerald-600' },
    pending: { label: 'Pending Approval', className: 'bg-amber-500' },
    rejected: { label: 'Rejected', className: 'bg-red-500' },
  };

  const handleFileSelect = (vehicleId) => {
    const ok = window.confirm(
      'Heads up: once a vehicle photo is uploaded it cannot be changed. ' +
      'This helps prevent unauthorized photo swaps after security verification.\n\n' +
      'Are you sure you want to upload now?'
    );
    if (!ok) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file && onUploadPicture) onUploadPicture(vehicleId, file);
    };
    input.click();
  };

  return (
    <Card className="shadow-lg border-0" data-testid="vehicles-card">
      <CardHeader className="bg-gk-navy-light text-white rounded-t-xl flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Car className="w-5 h-5" />
          My Vehicles
          <Badge variant="outline" className={`ml-1 border-white/40 ${fourWheelers.length >= maxCars ? 'text-red-300 border-red-300/60' : 'text-white'}`}>{fourWheelers.length}/{maxCars} cars</Badge>
          <Badge variant="outline" className={`border-white/40 ${twoWheelers.length >= maxBikes ? 'text-red-300 border-red-300/60' : 'text-white'}`}>{twoWheelers.length}/{maxBikes} bikes</Badge>
        </CardTitle>
        <Button data-testid="add-vehicle-button" size="sm" onClick={onAdd} className="bg-gk-teal hover:bg-gk-teal-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </CardHeader>
      <CardContent className="pt-4">
        {vehicles.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No vehicles registered</p>
        ) : (
          <div className="space-y-2">
            {vehicles.map((v) => {
              const status = statusConfig[v.approval_status] || statusConfig.approved;
              return (
                <div key={v.id} data-testid={`vehicle-${v.id}`} className={`flex items-center justify-between p-3 rounded-lg ${
                  v.approval_status === 'rejected' ? 'bg-red-50' : v.approval_status === 'pending' ? 'bg-amber-50' : 'bg-emerald-50'
                }`}>
                  <div className="flex items-center gap-3">
                    {v.vehicle_picture ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/vehicles/${v.id}/picture`}
                        alt={v.license_plate}
                        className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${v.vehicle_type === '2-wheeler' ? 'bg-amber-200' : 'bg-emerald-200'}`}
                      style={{ display: v.vehicle_picture ? 'none' : 'flex' }}
                      onClick={() => handleFileSelect(v.id)}
                      title="Upload vehicle photo"
                    >
                      <Camera className={`w-5 h-5 ${v.vehicle_type === '2-wheeler' ? 'text-amber-700' : 'text-emerald-700'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{v.make} {v.model}</p>
                        {v.is_ev && <Zap className="w-3.5 h-3.5 text-green-500" />}
                        <Badge variant="outline" className="text-xs">{v.vehicle_type}</Badge>
                        <Badge className={`${status.className} text-white text-[10px]`}>{status.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500"><span className="font-mono font-bold">{v.license_plate}</span> | {v.color}</p>
                      {v.source === 'community' && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded"
                          data-testid={`vehicle-pre-registered-${v.id}`}
                          title="Added by management for your villa"
                        >
                          <Building2 className="w-3 h-3" />
                          Pre-registered by management
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!v.vehicle_picture && (
                      <Button size="sm" variant="ghost" onClick={() => handleFileSelect(v.id)} className="text-slate-400 hover:text-slate-600" title="Upload photo">
                        <Upload className="w-4 h-4" />
                      </Button>
                    )}
                    {v.source !== 'community' && (
                      <Button size="sm" variant="ghost" onClick={() => onRemove(v.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export function HobbiesCard({ hobbies, predefinedHobbies, onChange, onAddCustom }) {
  const [saving, setSaving] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const toggle = async (hobby) => {
    const set = new Set(hobbies);
    if (set.has(hobby)) {
      set.delete(hobby);
    } else {
      if (set.size >= 20) {
        toast.error('Maximum 20 hobbies allowed');
        return;
      }
      set.add(hobby);
    }
    setSaving(true);
    try { await onChange(Array.from(set)); } finally { setSaving(false); }
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    const v = customValue.trim();
    if (!v) return;
    if (hobbies.some(h => h.toLowerCase() === v.toLowerCase())) {
      toast.error('Hobby already added');
      return;
    }
    if (hobbies.length >= 20) {
      toast.error('Maximum 20 hobbies allowed');
      return;
    }
    setSaving(true);
    try {
      await onAddCustom(v);
      setCustomValue('');
    } finally { setSaving(false); }
  };

  const selected = new Set(hobbies.map(h => h.toLowerCase()));
  const customHobbies = hobbies.filter(h => !predefinedHobbies.some(p => p.toLowerCase() === h.toLowerCase()));

  return (
    <Card className="shadow-lg border-0" data-testid="hobbies-card">
      <CardHeader className="bg-gk-navy-light text-white rounded-t-xl flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Heart className="w-5 h-5" />
          Hobbies & Interests
          <Badge variant="outline" className="ml-1 text-white border-white/40">{hobbies.length}/20</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        <p className="text-xs text-slate-500">Tap to select from the list, or add your own below.</p>
        <div className="flex flex-wrap gap-2">
          {predefinedHobbies.map((hobby) => {
            const isSelected = selected.has(hobby.toLowerCase());
            return (
              <button
                key={hobby}
                data-testid={`hobby-chip-${hobby.toLowerCase().replace(/\s+/g, '-')}`}
                type="button"
                disabled={saving}
                onClick={() => toggle(hobby)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  isSelected
                    ? 'bg-gk-teal text-white border-gk-teal'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-gk-teal hover:text-gk-teal'
                }`}
              >
                {hobby}
              </button>
            );
          })}
        </div>

        {customHobbies.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Your custom hobbies</p>
            <div className="flex flex-wrap gap-2">
              {customHobbies.map((hobby) => (
                <button
                  key={hobby}
                  data-testid={`hobby-custom-${hobby.toLowerCase().replace(/\s+/g, '-')}`}
                  type="button"
                  disabled={saving}
                  onClick={() => toggle(hobby)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-500 text-white border border-indigo-500 flex items-center gap-1.5"
                >
                  {hobby}
                  <Trash2 className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleCustomSubmit} className="flex gap-2 pt-2 border-t border-slate-100">
          <input
            data-testid="hobby-custom-input"
            type="text"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder="Add custom hobby..."
            maxLength={40}
            className="flex-1 h-9 px-3 text-sm rounded-md border border-slate-300 focus:border-gk-teal focus:outline-none"
          />
          <Button
            data-testid="hobby-add-custom-button"
            type="submit"
            size="sm"
            disabled={saving || !customValue.trim()}
            className="bg-gk-teal hover:bg-gk-teal-hover"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function DirectoryOptInCard({ showInDirectory, onChange, enabled, visibility, onToggleVisibility }) {
  if (!enabled) return null;

  const Toggle = ({ on, onClick, testId }) => (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        on ? 'bg-gk-teal' : 'bg-slate-300'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <Card className="shadow-lg border-0" data-testid="directory-optin-card">
      <CardHeader className="bg-gk-navy-light text-white rounded-t-xl pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Contact className="w-5 h-5" />
          Neighbors Directory
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-800">Show me in the directory</p>
            <p className="text-xs text-slate-500 mt-1 leading-snug">
              When enabled, other residents in this community can see your name and villa number.
            </p>
          </div>
          <Toggle on={showInDirectory} onClick={() => onChange(!showInDirectory)} testId="directory-optin-toggle" />
        </div>

        {showInDirectory && (
          <div className="pt-3 border-t border-slate-100 space-y-2.5">
            <p className="text-xs font-semibold text-slate-600">What else should neighbors see?</p>

            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Profile picture</span>
              <Toggle on={!!visibility?.profile_picture} onClick={() => onToggleVisibility('profile_picture', !visibility?.profile_picture)} testId="directory-vis-pic" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Email address</span>
              <Toggle on={!!visibility?.email} onClick={() => onToggleVisibility('email', !visibility?.email)} testId="directory-vis-email" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Phone number</span>
              <Toggle on={!!visibility?.phone} onClick={() => onToggleVisibility('phone', !visibility?.phone)} testId="directory-vis-phone" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Hobbies & interests</span>
              <Toggle on={!!visibility?.hobbies} onClick={() => onToggleVisibility('hobbies', !visibility?.hobbies)} testId="directory-vis-hobbies" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
